import { AxiosError } from 'axios';
import { ClickableOptionsCard } from 'core/Components';
import { getErrorMessage_v2 } from 'core/Utils';
import { Group, Modal, Stack, Switch, Text, Title, Tooltip, notifications } from 'ui';
import { IconInfo } from 'ui/Icons';

import { useBackerInfo, useUpdateBacker } from '../../../hooks/queries/Backer/backer';
import { useLedgerOwner, useUpdateLedgerOwner } from '../../../hooks/queries/Ledger/owner';

type Props = {
  opened: boolean;
  onClose: () => void;
};

const AnonymousDonationSwitch = () => {
  const { data: owner } = useLedgerOwner();
  const { mutate: updateOwner, isPending: isLoading, variables } = useUpdateLedgerOwner();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateOwner(
      {
        entry_privacy: event.currentTarget.checked ? 'entries_private' : 'entries_public',
      },
      {
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  };

  return (
    <Switch
      size="lg"
      checked={
        isLoading
          ? variables?.entry_privacy === 'entries_private'
          : owner?.entry_privacy === 'entries_private'
      }
      onChange={handleChange}
    />
  );
};

const ShareInfoSwitch = () => {
  const { data: backer } = useBackerInfo();
  const { mutate: updateBacker, isPending: isLoading, variables } = useUpdateBacker();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateBacker(
      { share_my_info: event.currentTarget.checked },
      {
        onError: (e) => {
          const error = e as Error | AxiosError;
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  };

  return (
    <Switch
      size="lg"
      checked={isLoading ? variables?.share_my_info : backer?.share_my_info}
      onChange={handleChange}
    />
  );
};

const PrivacyModalContent = () => {
  return (
    <Stack gap="lg">
      <Stack>
        <Title order={3}>General Settings</Title>
        <ClickableOptionsCard>
          <ClickableOptionsCard.Row
            title="Keep my identity private when donating cashback"
            rightSection={<AnonymousDonationSwitch />}
          />
          <ClickableOptionsCard.Row
            title={
              <Group>
                <Text size="sm" fw={500}>
                  Share my email and phone number with organizations
                </Text>
                <Tooltip label="Your contact info will always be shared when you purchase a membership, even if this setting is turned off.">
                  <IconInfo size={16} color="gray" />
                </Tooltip>
              </Group>
            }
            rightSection={<ShareInfoSwitch />}
          />
        </ClickableOptionsCard>
      </Stack>
    </Stack>
  );
};

const PrivacyModal = ({ opened, onClose }: Props) => {
  return (
    <Modal title="Privacy Settings" opened={opened} onClose={onClose}>
      <PrivacyModalContent />
    </Modal>
  );
};

export { PrivacyModal };
